import { type ReactNode } from 'react';
import {
	Modal as ChakraModal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	type ModalProps as ChakraModalProps,
} from '@chakra-ui/react';
import { useLayout } from '../../hooks/useLayout';

/**
 * Modal primitive — a Dialog shell with a fixed header / body / footer
 * contract. Use this for centered, focused tasks. For side panels that should
 * coexist with the page, use the `Drawer` primitive instead.
 *
 * Contract:
 * - `title`: header content (string or node)
 * - `footer`: action row (usually Buttons)
 * - `size`: Chakra modal size token. Defaults to `full` on mobile so forms
 *   stay usable on a phone-width window.
 */
export interface ModalProps extends Omit<ChakraModalProps, 'children'> {
	title?: ReactNode;
	footer?: ReactNode;
	children: ReactNode;
	showCloseButton?: boolean;
}

export const Modal = ({
	title,
	footer,
	children,
	showCloseButton = true,
	size,
	...rest
}: ModalProps) => {
	const layout = useLayout();
	const resolvedSize = size ?? (layout === 'mobile' ? 'full' : 'md');
	return (
		<ChakraModal isCentered size={resolvedSize} {...rest}>
			<ModalOverlay />
			<ModalContent>
				{title ? <ModalHeader>{title}</ModalHeader> : null}
				{showCloseButton ? <ModalCloseButton /> : null}
				<ModalBody>{children}</ModalBody>
				{footer ? <ModalFooter gap={3}>{footer}</ModalFooter> : null}
			</ModalContent>
		</ChakraModal>
	);
};

export default Modal;
